import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Loader, 
  Edit2, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Camera,
  AlertCircle,
  Save,
  Image as ImageIcon, 
  X, 
  MoveVertical, 
  Upload, 
  Grid3X3, 
  Info, 
  CheckCircle2,
  Sparkles,
  Star,
  Sun,
  Home,
  Eye,
  Lightbulb
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';

// Importar componentes reutilizables de publicación
import UbiPublish from './publishes/UbiPublish';
import LugPublish from './publishes/LugPublish';
// Comentar temporalmente hasta que se integren los componentes correctos
// import InfPublish from './publishes/InfPublish';
// import CarPublish from './publishes/CarPublish';

// Importar componente de experiencia de alquiler
import SaleExperienceModal from '../components/SaleExperienceModal';

// URL de Cloudflare para imágenes
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Función de debounce para limitar la frecuencia de ejecución
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const EditRent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();

  // Estados para manejo de la publicación
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [propertyStatus, setPropertyStatus] = useState('Activo');
  const [savingChanges, setSavingChanges] = useState(false);
  const [deletingProperty, setDeletingProperty] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Estados para gestión de imágenes (nuevo)
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(null);
  const [showTips, setShowTips] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(false);

  // Modal para experiencia de alquiler
  const [showRentExperienceModal, setShowRentExperienceModal] = useState(false);

  // Tips de fotografía profesional
  const photographyTips = [
    {
      icon: Sun,
      title: "Luz Natural",
      description: "Fotografía durante el día con luz natural. Evita el flash directo.",
      color: "yellow"
    },
    {
      icon: Eye,
      title: "Ángulos Estratégicos",
      description: "Captura desde las esquinas para mostrar la amplitud del espacio.",
      color: "blue"
    },
    {
      icon: Home,
      title: "Espacios Ordenados",
      description: "Asegúrate de que las habitaciones estén limpias y ordenadas.",
      color: "green"
    },
    {
      icon: Grid3X3,
      title: "Composición",
      description: "Usa la regla de los tercios para crear imágenes más atractivas.",
      color: "purple"
    }
  ];

  // Recomendaciones de fotos esenciales
  const essentialPhotos = [
    { name: "Fachada exterior", priority: "Alta", icon: Home },
    { name: "Sala principal", priority: "Alta", icon: Home },
    { name: "Cocina", priority: "Alta", icon: Home },
    { name: "Habitación principal", priority: "Alta", icon: Home },
    { name: "Baño principal", priority: "Media", icon: Home },
    { name: "Vista desde ventanas", priority: "Media", icon: Eye },
    { name: "Áreas exteriores", priority: "Baja", icon: Sun }
  ];

  // Función para obtener la URL completa de la imagen
  const getFullImageUrl = useCallback((imageUrl) => {
    if (!imageUrl) return '/placeholder-property.jpg';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
  }, []);

  // Cargar datos del alquiler
  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        setLoading(true);
        console.log('Cargando alquiler con ID:', id);
        const response = await fetch(`https://backend-production-1786.up.railway.app/api/alquileres/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('No se pudo cargar el alquiler');
        }

        const data = await response.json();
        console.log('Datos de alquiler obtenidos:', data);

        // Verificar si el usuario actual es el propietario
        if (data.id_vendedor !== user.id) {
          toast({
            title: 'Acceso denegado',
            description: 'No tienes permiso para editar este alquiler',
            variant: 'destructive'
          });
          navigate('/mis_propiedades');
          return;
        }

        // Convertir lugares_cercanos y servicios_basicos a arrays si son strings
        const processedData = {
          ...data,
          lugares_cercanos: Array.isArray(data.lugares_cercanos) 
            ? data.lugares_cercanos 
            : (data.lugares_cercanos || []),
          servicios_basicos: Array.isArray(data.servicios_basicos) 
            ? data.servicios_basicos 
            : (data.servicios_basicos || []),
          area_completa: data.area_total === data.area_ocupada,
          operacion: 'alquiler' // Asegurar que la operación sea alquiler
        };

        setProperty(processedData);
        
        // Procesar imágenes
        setImages(data.imagenes.map(img => ({
          url: getFullImageUrl(img),
          file: null,
          original: img
        })));
        
        setPropertyStatus(data.estado);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar alquiler:', err);
        setError(err.message);
        setLoading(false);
        toast({
          title: 'Error',
          description: err.message,
          variant: 'destructive'
        });
        navigate('/mis_propiedades');
      }
    };

    if (user && token) {
      fetchPropertyDetails();
    }
  }, [id, user, token, navigate, toast, getFullImageUrl]);

  // Configuración de colores para tips
  const getColorConfig = (color) => {
    const configs = {
      yellow: { bg: 'bg-yellow-50', icon: 'text-yellow-600', border: 'border-yellow-200' },
      blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
      green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
      purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-200' }
    };
    return configs[color] || configs.blue;
  };

  // Funciones para gestión de imágenes
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadProgress(true);
    
    const newImages = files.map(file => ({
      url: URL.createObjectURL(file),
      file,
      original: null
    }));

    // Limitar a 10 imágenes en total
    const updatedImages = [...images, ...newImages].slice(0, 10);
    setImages(updatedImages);
    
    setTimeout(() => setUploadProgress(false), 1000);
  };

  const removeImage = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  // Función para reordenar las imágenes
  const reorderImages = (startIndex, endIndex) => {
    const newImages = [...images];
    const [movedImage] = newImages.splice(startIndex, 1);
    newImages.splice(endIndex, 0, movedImage);
    return newImages;
  };

  // Función para manejar el inicio del arrastre
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    if (e.dataTransfer.setDragImage) {
      const emptyDiv = document.createElement('div');
      emptyDiv.style.display = 'none';
      document.body.appendChild(emptyDiv);
      e.dataTransfer.setDragImage(emptyDiv, 0, 0);
      setTimeout(() => {
        document.body.removeChild(emptyDiv);
      }, 0);
    }
    e.dataTransfer.effectAllowed = "move";
  };

  // Función para manejar el arrastre sobre otro elemento
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setIsDraggingOver(index);
  };

  // Función para manejar cuando se termina de arrastrar fuera de un elemento
  const handleDragLeave = () => {
    setIsDraggingOver(null);
  };

  // Función para manejar el drop (soltar)
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setIsDraggingOver(null);
      return;
    }

    const reorderedImages = reorderImages(draggedIndex, dropIndex);
    setImages(reorderedImages);
    
    setDraggedIndex(null);
    setIsDraggingOver(null);
  };

  // Manejar cambios de estado de alquiler
  const handleStatusChange = (status) => {
    setPropertyStatus(status);
    
    // Si el estado cambia a Alquilado, mostrar modal de experiencia
    if (status === 'Alquilado') {
      setShowRentExperienceModal(true);
    }
  };

  // Componente para manejar errores de carga de imágenes
  const PropertyImage = ({ url, alt, className }) => {
    const [error, setError] = useState(false);
  
    const handleError = () => {
      setError(true);
    };
  
    return (
      <img
        src={error ? '/placeholder-property.jpg' : url}
        alt={alt}
        className={className}
        onError={handleError}
      />
    );
  };

  // Función para manejar cambios en el formulario (con log espaciado)
  const logFieldChange = (name, value) => {
    console.log(`Campo actualizado: ${name} = ${value}`);
  };

  // Versión con debounce para evitar muchos logs
  const debouncedLogChange = useCallback(
    debounce(logFieldChange, 1000), // Espera 1 segundo antes de mostrar el log
    []
  );

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProperty(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Log con debounce para evitar sobrecargar la consola
    debouncedLogChange(name, value);
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value) || 0;
    
    // Convertir entre soles y dólares según tasa de cambio (ejemplo: 3.7)
    const exchangeRate = 3.7;
    
    if (name === 'precio_mensual') {
      setProperty(prev => ({
        ...prev,
        precio_mensual: numericValue,
        precio_dolares: (numericValue / exchangeRate).toFixed(2)
      }));
    } else if (name === 'precio_dolares') {
      setProperty(prev => ({
        ...prev,
        precio_dolares: numericValue,
        precio_mensual: (numericValue * exchangeRate).toFixed(2)
      }));
    } else if (name === 'mantenimiento') {
      setProperty(prev => ({
        ...prev,
        mantenimiento: numericValue
      }));
    }
    
    // Log con debounce
    debouncedLogChange(name, numericValue);
  };

  // Cálculo del área total cuando cambia largo o ancho
  useEffect(() => {
    if (property && property.largo_terreno && property.ancho_terreno) {
      const largo = parseFloat(property.largo_terreno) || 0;
      const ancho = parseFloat(property.ancho_terreno) || 0;
      const area = (largo * ancho).toFixed(2);
      
      setProperty(prev => ({
        ...prev,
        area_total: area,
        // Si área completa está marcada, actualizar área ocupada también
        ...(prev.area_completa ? { area_ocupada: area } : {})
      }));
    }
  }, [property?.largo_terreno, property?.ancho_terreno]);

  // Guardar cambios en el alquiler
  const handleSaveChanges = async () => {
    try {
      setSavingChanges(true);

      // Preparar FormData para envío de imágenes
      const formData = new FormData();
      
      // Añadir datos básicos del alquiler
      if (property) {
        // Campos de texto
        formData.append('id', property.id);
        formData.append('tipo', property.tipo || '');
        formData.append('titulo', property.titulo || '');
        formData.append('descripcion_corta', property.descripcion_corta || '');
        formData.append('ubicacion', property.ubicacion || '');
        formData.append('mapa_direccion', property.mapa_direccion || '');
        formData.append('operacion', 'alquiler'); // Forzar tipo de operación
        
        // Campos numéricos
        formData.append('precio_mensual', property.precio_mensual || 0);
        formData.append('precio_dolares', property.precio_dolares || 0);
        formData.append('mantenimiento', property.mantenimiento || 0);
        formData.append('largo_terreno', property.largo_terreno || 0);
        formData.append('ancho_terreno', property.ancho_terreno || 0);
        formData.append('area_total', property.area_total || 0);
        formData.append('area_ocupada', property.area_ocupada || 0);
        formData.append('habitaciones', property.habitaciones || 0);
        formData.append('banos', property.banos || 0);
        formData.append('antiguedad', property.antiguedad || 0);
        
        // Campos booleanos
        formData.append('estacionamiento', property.estacionamiento ? 'si' : 'no');
        formData.append('areas_verdes', property.areas_verdes || 'no');
        
        // Arrays
        formData.append('lugares_cercanos', JSON.stringify(property.lugares_cercanos || []));
        formData.append('servicios_basicos', JSON.stringify(property.servicios_basicos || []));
      }
      
      // Añadir estado actualizado
      formData.append('estado', propertyStatus);

      // Añadir imágenes originales que se mantienen en el orden correcto
      const imagesToKeep = images
        .filter(img => img.original)
        .map(img => img.original);
      
      formData.append('imagenes_originales', JSON.stringify(imagesToKeep));
      
      // Añadir nuevas imágenes
      images
        .filter(img => img.file)
        .forEach((img) => {
          formData.append('nuevas_imagenes', img.file);
        });

      // Debug - Mostrar lo que se va a enviar
      console.log('Enviando datos de formulario:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Enviar datos al servidor
      const response = await fetch(`https://backend-production-1786.up.railway.app/api/alquileres/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar el alquiler');
      }

      // Mostrar mensaje de éxito
      toast({
        title: 'Alquiler actualizado',
        description: 'Los cambios se han guardado correctamente',
      });

      // Redirigir a la página de propiedades
      navigate('/mis_propiedades');
    } catch (error) {
      console.error('Error al guardar los cambios:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSavingChanges(false);
    }
  };

  // Eliminar alquiler
  const handleDeleteProperty = async () => {
    try {
      setDeletingProperty(true);
      
      const response = await fetch(`https://backend-production-1786.up.railway.app/api/alquileres/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar el alquiler');
      }

      // Mostrar mensaje de éxito
      toast({
        title: 'Alquiler eliminado',
        description: 'El alquiler ha sido eliminado correctamente'
      });

      // Redirigir a la página de propiedades
      navigate('/mis_propiedades');
    } catch (error) {
      console.error('Error al eliminar el alquiler:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setDeletingProperty(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Manejar experiencia de alquiler exitosa
  const handleRentExperienceSuccess = async (data) => {
    try {
      // Actualizar el estado del alquiler a "Alquilado"
      setPropertyStatus('Alquilado');
      
      // Mostrar notificación
      toast({
        title: 'Experiencia registrada',
        description: 'El alquiler ha sido marcado como alquilado.',
        variant: 'default'
      });
      
      // Guardar los cambios automáticamente (no esperamos a que termine)
      handleSaveChanges();
      
      // Cerrar modal
      setShowRentExperienceModal(false);
      
      // Redirigir a mis propiedades después de un breve retraso
      // para que el usuario alcance a ver la notificación
      setTimeout(() => {
        navigate('/mis_propiedades');
      }, 1500);
      
    } catch (error) {
      console.error('Error al procesar experiencia de alquiler:', error);
      toast({
        title: 'Error',
        description: 'Hubo un problema al procesar el alquiler',
        variant: 'destructive'
      });
    }
  };

  // Renderizar contenido de la página de edición
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="animate-spin text-blue-500 w-12 h-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/mis_propiedades')}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
          >
            Volver a Mis Publicaciones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 pt-20 pb-10 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6">
        {/* Estado de la Publicación */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4">Estado del Alquiler</h2>
          <div className="flex space-x-4">
            <button
              onClick={() => handleStatusChange('Activo')}
              className={`px-4 py-2 rounded-lg transition-all ${
                propertyStatus === 'Activo' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-green-100'
              }`}
            >
              Activo
            </button>
            <button
              onClick={() => handleStatusChange('Alquilado')}
              className={`px-4 py-2 rounded-lg transition-all ${
                propertyStatus === 'Alquilado' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-blue-100'
              }`}
            >
              Alquilado
            </button>
            <button
              onClick={() => handleStatusChange('Suspendido')}
              className={`px-4 py-2 rounded-lg transition-all ${
                propertyStatus === 'Suspendido' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-orange-100'
              }`}
            >
              Suspendido
            </button>
          </div>
        </div>

        {/* Gestión de Imágenes - Rediseñado */}
        <div className="space-y-8 mb-8">
          {/* Header elegante */}
          <div className="text-center mb-8">
            <div className="relative mb-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-pink-500 rounded-full"></div>
                <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl shadow-lg">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-purple-500 rounded-full"></div>
              </div>
              
              <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 
                             bg-clip-text text-transparent mb-3">
                Galería de Imágenes
              </h2>
              
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
                <div className="w-16 h-0.5 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full"></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-75"></div>
              </div>
            </div>
            
            <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
              Sube fotos de alta calidad para destacar tu alquiler
            </p>
          </div>

          {/* Tips de fotografía profesional */}
          {showTips && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <Lightbulb className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-800">Tips para Fotos Profesionales</h3>
                    <p className="text-blue-600 text-sm">Maximiza el atractivo de tu alquiler</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTips(false)}
                  className="p-2 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-blue-600" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {photographyTips.map((tip, index) => {
                  const colors = getColorConfig(tip.color);
                  return (
                    <div
                      key={index}
                      className={`p-4 ${colors.bg} border ${colors.border} rounded-xl transition-all duration-300 hover:shadow-md`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <tip.icon className={`w-5 h-5 ${colors.icon}`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-1">{tip.title}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Fotos recomendadas */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Fotos Recomendadas
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {essentialPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm
                        ${photo.priority === 'Alta' 
                          ? 'bg-red-50 text-red-700' 
                          : photo.priority === 'Media' 
                          ? 'bg-yellow-50 text-yellow-700' 
                          : 'bg-green-50 text-green-700'
                        }`}
                    >
                      <photo.icon className="w-4 h-4" />
                      <span className="flex-1">{photo.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full
                        ${photo.priority === 'Alta' 
                          ? 'bg-red-100 text-red-600' 
                          : photo.priority === 'Media' 
                          ? 'bg-yellow-100 text-yellow-600' 
                          : 'bg-green-100 text-green-600'
                        }`}>
                        {photo.priority}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Área de carga principal */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-pink-50 to-purple-50 border-b border-gray-100 rounded-t-2xl">
              <div className="p-2 bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl">
                <Upload className="h-6 w-6 text-pink-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Subir Imágenes</h3>
                <p className="text-gray-500 text-sm">
                  {images.length} imagen{images.length !== 1 ? 'es' : ''} seleccionada{images.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="p-6">
              {/* Zona de drop elegante */}
              <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center 
                              hover:border-purple-400 hover:bg-purple-50/50 transition-all duration-300">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="images"
                />
                
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl">
                      <ImageIcon className="w-12 h-12 text-purple-600" />
                    </div>
                  </div>
                  
                  <div>
                    <label
                      htmlFor="images"
                      className="inline-flex items-center gap-2 cursor-pointer bg-gradient-to-r from-purple-600 to-pink-600 
                               text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg
                               hover:shadow-xl hover:scale-105 transition-all duration-300"
                    >
                      {uploadProgress ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Procesando...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span>Seleccionar Imágenes</span>
                        </>
                      )}
                    </label>
                  </div>
                  
                  <div className="text-sm text-gray-500 space-y-1">
                    <p>o arrastra y suelta las imágenes aquí</p>
                    <p className="text-xs">Formatos: JPG, PNG, WEBP • Máximo 10MB por imagen</p>
                  </div>
                </div>
              </div>

              {/* Galería de imágenes */}
              {images.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <Grid3X3 className="w-5 h-5" />
                      Vista Previa ({images.length})
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MoveVertical className="w-4 h-4" />
                      <span>Arrastra para reordenar</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg border-2 transition-all duration-300
                          ${draggedIndex === index 
                            ? 'opacity-50 border-dashed border-purple-500 scale-105' 
                            : isDraggingOver === index 
                            ? 'border-purple-500 shadow-xl scale-105' 
                            : 'border-gray-200 hover:border-purple-300 hover:shadow-xl hover:scale-105'
                          }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={() => {
                          setDraggedIndex(null);
                          setIsDraggingOver(null);
                        }}
                      >
                        {/* Indicador de posición */}
                        <div className="absolute top-3 left-3 z-20">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm shadow-lg
                            ${index === 0 
                              ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' 
                              : 'bg-black/70 text-white'
                            }`}>
                            {index === 0 ? <Star className="w-4 h-4" /> : index + 1}
                          </div>
                        </div>

                        {/* Imagen */}
                        <div className="aspect-[4/3] overflow-hidden">
                          <PropertyImage
                            url={image.url}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>

                        {/* Overlay con controles */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent 
                                      opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          
                          {/* Botón eliminar */}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 rounded-full 
                                     text-white shadow-lg transition-all duration-200 hover:scale-110"
                          >
                            <X className="w-4 h-4" />
                          </button>

                          {/* Indicador de arrastre */}
                          <div className="absolute bottom-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full 
                                        text-white cursor-move transition-all duration-200 hover:scale-110">
                            <MoveVertical className="w-4 h-4" />
                          </div>

                          {/* Badge de imagen principal */}
                          {index === 0 && (
                            <div className="absolute bottom-3 left-3 px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 
                                          rounded-full text-white text-xs font-semibold flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              <span>Principal</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Información adicional */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Info className="w-4 h-4 text-blue-500" />
                      <span>
                        <strong>Imagen principal:</strong> La primera imagen será la portada de tu publicación. 
                        Asegúrate de que sea la más atractiva.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Formularios de Edición */}
        <div className="space-y-6">
          {/* Información principal - adaptada para alquileres */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">Información Principal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 mb-2">Título</label>
                <input
                  type="text"
                  name="titulo"
                  value={property.titulo || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Tipo de Propiedad</label>
                <select
                  name="tipo"
                  value={property.tipo || 'departamento'}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="departamento">Departamento</option>
                  <option value="casa">Casa</option>
                  <option value="habitacion">Habitación</option>
                  <option value="oficina">Oficina</option>
                  <option value="local">Local Comercial</option>
                  <option value="terreno">Terreno</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 mb-2">Descripción</label>
                <textarea
                  name="descripcion_corta"
                  value={property.descripcion_corta || ''}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Alquiler Mensual (S/.)</label>
                <input
                  type="number"
                  name="precio_mensual"
                  value={property.precio_mensual || ''}
                  onChange={handleCurrencyChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Precio en Dólares (USD)</label>
                <input
                  type="number"
                  name="precio_dolares"
                  value={property.precio_dolares || ''}
                  onChange={handleCurrencyChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Mantenimiento Mensual (S/.)</label>
                <input
                  type="number"
                  name="mantenimiento"
                  value={property.mantenimiento || ''}
                  onChange={handleCurrencyChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          {/* Ubicación usando UbiPublish */}
          <UbiPublish
            formData={property}
            setFormData={setProperty}
          />

          {/* Características */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">Características</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 mb-2">Área Total (m²)</label>
                <input
                  type="number"
                  name="area_total"
                  value={property.area_total || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Área Construida (m²)</label>
                <input
                  type="number"
                  name="area_ocupada"
                  value={property.area_ocupada || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="lg:col-span-1">
                <label className="block text-gray-700 mb-2">Habitaciones</label>
                <input
                  type="number"
                  name="habitaciones"
                  value={property.habitaciones || ''}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Baños</label>
                <input
                  type="number"
                  name="banos"
                  value={property.banos || ''}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Antigüedad</label>
                <select
                  name="antiguedad"
                  value={property.antiguedad || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar</option>
                  <option value="nuevo">A estrenar</option>
                  <option value="1-5">1-5 años</option>
                  <option value="6-10">6-10 años</option>
                  <option value="11-20">11-20 años</option>
                  <option value="21+">Más de 20 años</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="estacionamiento"
                  name="estacionamiento"
                  checked={property.estacionamiento === 'si'}
                  onChange={(e) => setProperty(prev => ({
                    ...prev,
                    estacionamiento: e.target.checked ? 'si' : 'no'
                  }))}
                  className="mr-2"
                />
                <label htmlFor="estacionamiento" className="text-gray-700">
                  Estacionamiento
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="areas_verdes"
                  name="areas_verdes"
                  checked={property.areas_verdes === 'si'}
                  onChange={(e) => setProperty(prev => ({
                    ...prev,
                    areas_verdes: e.target.checked ? 'si' : 'no'
                  }))}
                  className="mr-2"
                />
                <label htmlFor="areas_verdes" className="text-gray-700">
                  Áreas Verdes
                </label>
              </div>
            </div>
          </div>
          
          {/* Lugares cercanos y servicios usando LugPublish */}
          <LugPublish
            formData={{
              ...property,
              operacion: 'alquiler' // Forzar modo alquiler para mostrar los servicios adecuados
            }}
            setFormData={setProperty}
          />
        </div>

        {/* Botones de Acción */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={() => setShowDeleteConfirmation(true)}
            className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
            disabled={deletingProperty}
          >
            {deletingProperty ? (
              <Loader className="animate-spin w-4 h-4" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Eliminar Alquiler
          </button>
          
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/mis_propiedades')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveChanges}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
              disabled={savingChanges}
            >
              {savingChanges ? (
                <Loader className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar Cambios
            </button>
          </div>
        </div>

        {/* Modal de Experiencia de Alquiler */}
        <SaleExperienceModal 
          isOpen={showRentExperienceModal}
          onClose={() => setShowRentExperienceModal(false)}
          propertyId={id}
          propertyTitle={property?.titulo || 'Alquiler'}
          onSuccess={handleRentExperienceSuccess}
          isRental={true} // Indicar que es para alquiler
        />

        {/* Modal de Confirmación de Eliminación */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4 text-red-600">Confirmar Eliminación</h2>
              <p className="mb-6 text-gray-700">
                ¿Estás seguro de que deseas eliminar este alquiler? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  disabled={deletingProperty}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteProperty}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                  disabled={deletingProperty}
                >
                  {deletingProperty ? (
                    <Loader className="animate-spin w-4 h-4" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default EditRent;